import React from 'react';

import useModal from '@components/modals/hooks/useModal';

import { ModalTypes } from '@core/types/Modals';

import SubscriptionConfirmation from '@components/modals/SubscriptionConfirmation';

type UseSubscriptionConfirmationProps = {
    widen?: boolean;
    className?: string;
};

type UseSubscriptionConfirmationConfirmProps = {
    title: string;
    text: string;
    confirmationButtonText: string;
    onConfirm?: () => void;
    onCancel?: () => void;
};

/**
 * React hook for showing a confirmation dialog for
 *
 * Use like so:
 *
 * const confirm = useConfirmation();
 *
 * const confirmSomething = () => {
 *     confirm({
 *         text: "Are you sure?",
 *         onConfirm: () => alert("You said yes!"),
 *         onCancel: () => alert("You said no!"),
 *     });
 * };
 *
 * return (
 *     <button type="button" onClick={confirmSomething}>Confirm Something for me please!</button>
 * );
 */
export const useSubscriptionConfirmation = ({
    widen = false,
    className = '',
}: UseSubscriptionConfirmationProps = {}) => {
    const { newModal } = useModal({ desktop: ModalTypes.Center, mobile: ModalTypes.Center });

    const confirm = ({
        title,
        text,
        confirmationButtonText,
        onConfirm,
        onCancel,
    }: UseSubscriptionConfirmationConfirmProps) => {
        return new Promise<boolean>(resolve => {
            const _onConfirm = () => {
                resolve(true);
                onConfirm?.();
            };

            const _onCancel = () => {
                resolve(false);
                onCancel?.();
            };

            newModal(
                <SubscriptionConfirmation
                    title={title}
                    text={text}
                    confirmationButtonText={confirmationButtonText}
                    onConfirm={_onConfirm}
                    onCancel={_onCancel}
                    className={className}
                />,
                {
                    hideButton: true,
                    onClose: _onCancel,
                    className: 'confirmation-modal',
                    sectionClassName: 'confirmation-modal-section',
                    widen,
                    addShadow: widen,
                }
            );
        });
    };

    return confirm;
};

export default useSubscriptionConfirmation;
